import { ReactElement, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Permission } from "react-ai-platform-auth";

import { Authenticate, useAuthContext, withAuthorize } from "../authHelper";
import {
  ASSESSMENT,
  ACCOUNT,
  GROUP_ASSESSMENT,
  JOBS,
  LEGAL,
  ORGANIZATIONS,
  PROJECTS,
  REVISIONS,
  SUPPORT,
  USERS,
  LIONBRIDGE_CMP,
} from "../routes";
import LoadingComponent from "../components/LoadingIndicators/LoadingComponent";
import lazyWithRetry from "../utilities/lazyWithRetry";
import useFeatureFlag from "../hooks/useFeatureFlag";

const AcceptAgreement = lazyWithRetry(
  () => import("../scenes/Legal/AcceptAgreement"),
  "AcceptAgreement"
);
const AssessmentCreate = lazyWithRetry(
  () => import("../scenes/Assessment/Create"),
  "AssessmentCreate"
);
const GroupAssessmentCreate = lazyWithRetry(
  () => import("../scenes/Assessment/CreateGroup"),
  "GroupAssessmentCreate"
);
const AssessmentList = lazyWithRetry(
  () => import("../scenes/Assessment/AssessmentList"),
  "AssessmentList"
);
const AssessmentContainer = lazyWithRetry(
  () => import("../scenes/Assessment/Container"),
  "AssessmentContainer"
);
const GroupAssessmentList = lazyWithRetry(
  () => import("../scenes/Assessment/GroupAssessmentList"),
  "GroupAssessmentList"
);
const AccountExpirationAlert = lazyWithRetry(
  () => import("../components/Alerts/AccountExpirationAlert"),
  "AccountExpirationAlert"
);
const GroupAssessmentContainer = lazyWithRetry(
  () => import("../scenes/Assessment/GroupContainer"),
  "GroupAssessmentContainer"
);
const RevisionBatchList = lazyWithRetry(
  () => import("../scenes/Revision/BatchList"),
  "RevisionBatchList"
);
const RevisionBatchDetails = lazyWithRetry(
  () => import("../scenes/Revision/BatchDetails"),
  "RevisionBatchDetails"
);
const UserList = lazyWithRetry(() => import("../scenes/User/UserList"), "UserList");
const UserDetailsScene = lazyWithRetry(
  () => import("../scenes/User/UserDetailsScene"),
  "UserDetailsScene"
);
const OrganizationList = lazyWithRetry(
  () => import("../scenes/Organization/OrganizationList"),
  "OrganizationList"
);
const OrganizationDetailsScene = lazyWithRetry(
  () => import("../scenes/Organization/OrganizationDetailsScene"),
  "OrganizationDetailsScene"
);
const UserProfile = lazyWithRetry(() => import("../scenes/Account/UserProfile"), "UserProfile");
const Support = lazyWithRetry(() => import("../scenes/Support/SupportTabs"), "Support");
const ProjectManagementRoutes = lazyWithRetry(
  () => import("./ProjectManagementRoutes"),
  "ProjectManagementRoutes"
);
const ProjectsList = lazyWithRetry(() => import("../scenes/Project/ProjectsList"), "ProjectsList");
const JobsList = lazyWithRetry(() => import("../scenes/Project/JobsList"), "JobsList");
const OldSupport = lazyWithRetry(() => import("../scenes/Support/Support"), "OldSupport");
const RedirectToProjectsList = (): ReactElement => <Redirect to={PROJECTS.LIST} />;
const RedirectToJobsList = (): ReactElement => <Redirect to={LIONBRIDGE_CMP.FIND_JOBS} />;
const RedirectToAssessmentList = (): ReactElement => <Redirect to={ASSESSMENT.LIST} />;
const RedirectToGroupAssessmentList = (): ReactElement => <Redirect to={GROUP_ASSESSMENT.LIST} />;

const AuthLoadingHolder = ({ children }: { children: ReactElement }): ReactElement => {
  const { isLoading } = useAuthContext();
  return isLoading ? <LoadingComponent title="Authenticating" /> : children;
};

const DynamicRoutes = (): ReactElement => {
  const isGroupAssessmentEnabled = useFeatureFlag("AP_ENABLE_GROUP_ASSESSMENTS");

  return (
    <>
      <AuthLoadingHolder>
        <Suspense fallback={<LoadingComponent title="Loading" />}>
          <Authenticate>
            <AccountExpirationAlert />
            <Switch>
              <Route
                exact
                path={PROJECTS.LIST}
                component={withAuthorize(
                  () => (
                    <ProjectsList />
                  ),
                  Permission.READ_PROJECTS,
                  RedirectToJobsList
                )}
              />
              <Route
                path={PROJECTS.LIST}
                exact={false}
                component={withAuthorize(
                  () => (
                    <ProjectManagementRoutes />
                  ),
                  Permission.READ_PROJECTS,
                  RedirectToJobsList
                )}
              />
            </Switch>
            <Route
              exact
              path={JOBS.LIST}
              component={withAuthorize(JobsList, Permission.WORK_PROJECTS)}
            />
            <Route
              exact
              path={REVISIONS.BATCH_LIST}
              component={withAuthorize(
                () => (
                  <RevisionBatchList />
                ),
                Permission.UPDATE_PROJECTS,
                RedirectToProjectsList
              )}
            />
            <Route
              exact
              path={REVISIONS.BATCH_DETAILS}
              component={withAuthorize(RevisionBatchDetails, Permission.UPDATE_PROJECTS)}
            />
            <Route
              exact
              path={USERS.LIST}
              component={withAuthorize(UserList, Permission.READ_USERS, RedirectToProjectsList)}
            />
            <Route
              exact
              path={USERS.DETAILS}
              component={withAuthorize(
                UserDetailsScene,
                Permission.READ_USERS,
                RedirectToProjectsList
              )}
            />
            <Route
              exact
              path={ORGANIZATIONS.LIST}
              component={withAuthorize(
                OrganizationList,
                Permission.READ_ORGS,
                RedirectToProjectsList
              )}
            />
            <Route
              exact
              path={ORGANIZATIONS.DETAILS}
              component={withAuthorize(
                OrganizationDetailsScene,
                Permission.UPDATE_ORGS,
                RedirectToProjectsList
              )}
            />
            <Route exact path={LEGAL.ACCEPT_AGREEMENTS} component={AcceptAgreement} />
            <Route exact path={ACCOUNT.USER_PROFILE} component={UserProfile} />
            <Route
              exact
              path={SUPPORT.FAQ}
              component={withAuthorize(Support, Permission.CREATE_SUPPORT_TICKETS, () => (
                <OldSupport />
              ))}
            />
            <Route
              exact
              path={ASSESSMENT.LIST}
              component={withAuthorize(AssessmentList, Permission.WORK_ASSESSMENTS)}
            />
            <Switch>
              <Route exact path={GROUP_ASSESSMENT.LIST}>
                {isGroupAssessmentEnabled ? (
                  withAuthorize(GroupAssessmentList, Permission.WORK_ASSESSMENTS)
                ) : (
                  <RedirectToAssessmentList />
                )}
              </Route>
              <Route exact path={GROUP_ASSESSMENT.CREATE}>
                {isGroupAssessmentEnabled ? (
                  withAuthorize(GroupAssessmentCreate, Permission.CREATE_ASSESSMENTS)
                ) : (
                  <RedirectToGroupAssessmentList />
                )}
              </Route>
              <Route path={GROUP_ASSESSMENT.DETAILS} component={GroupAssessmentContainer} />
              <Route
                exact
                path={ASSESSMENT.CREATE}
                component={withAuthorize(
                  AssessmentCreate,
                  Permission.CREATE_ASSESSMENTS,
                  RedirectToAssessmentList
                )}
              />
              <Route path={ASSESSMENT.DETAILS} component={AssessmentContainer} />
            </Switch>
          </Authenticate>
        </Suspense>
      </AuthLoadingHolder>
    </>
  );
};

export default DynamicRoutes;
